import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, CalendarDays } from 'lucide-react';
import { useAuth } from '../store/auth';
import { getLeaveBalances, type LeaveBalanceItem } from '../api/leave';
import { Card } from '../components/ui/card';
import EmptyState from '../components/ui/EmptyState';
import LoadingState from '../components/ui/LoadingState';
import PageHeader from '../components/ui/PageHeader';
import { cn } from '@/lib/utils';

function formatBalanceDays(n: number): string {
  return Number.isInteger(n) ? n.toFixed(0) : n.toFixed(2);
}

export default function LeaveBalance() {
  const { user } = useAuth();
  const [balances, setBalances] = useState<LeaveBalanceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(true);

  const year = new Date().getFullYear();

  useEffect(() => {
    if (!user?.employeeId) {
      setError('No employee profile linked.');
      setLoading(false);
      return;
    }
    getLeaveBalances(user.employeeId, year).then((res) => {
      if (res.data?.data) setBalances(res.data.data);
      else if (res.error) setError(res.error);
      setLoading(false);
    });
  }, [user?.employeeId, year]);

  const annualSummary = useMemo(() => {
    const annual = balances.find((b) => b.leaveType.type === 'ANNUAL');
    return {
      remaining: annual ? Number(annual.remainingDays) : 0,
      used: annual ? Number(annual.usedDays) : 0,
    };
  }, [balances]);

  const employeeName = user?.name || user?.email || 'My leave balance';

  if (loading) return <LoadingState label="Loading leave balance…" />;

  return (
    <div className="space-y-6">
      <PageHeader title="Leave balance" subtitle={`${year} entitlement overview`} />
      {error && (
        <div className="rounded-xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}
      {balances.length === 0 && !error && (
        <EmptyState
          icon={CalendarDays}
          title="No leave balance data"
          description="Your leave entitlements for this year will appear here once HR sets them up."
        />
      )}

      {balances.length > 0 && (
        <Card className="overflow-hidden">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="flex w-full items-center justify-between gap-3 p-4 text-left"
            aria-expanded={open}
          >
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold">{employeeName}</p>
              <p className="text-sm text-muted-foreground">
                {year} · {balances.length} leave type{balances.length === 1 ? '' : 's'}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <div className="text-right">
                <p className="text-lg font-bold text-primary">
                  {formatBalanceDays(annualSummary.remaining)}
                </p>
                <p className="text-xs text-muted-foreground">days left</p>
              </div>
              <ChevronDown
                className={cn('h-5 w-5 text-muted-foreground transition-transform', open && 'rotate-180')}
              />
            </div>
          </button>

          {open && (
            <ul className="divide-y border-t">
              {balances.map((b) => {
                const total = b.totalAvailable ?? b.allocatedDays ?? 0;
                const used = Number(b.usedDays);
                const remaining = Number(b.remainingDays);
                const pct = total > 0 ? Math.min(100, (used / total) * 100) : 0;
                return (
                  <li key={`${b.leaveType.id}-${b.year}`} className="px-4 py-4">
                    <div className="flex items-center justify-between">
                      <p className="font-medium">{b.leaveType.displayName}</p>
                      <p className="text-sm font-semibold text-primary tabular-nums">
                        {formatBalanceDays(remaining)} left
                      </p>
                    </div>
                    <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full rounded-full bg-primary transition-all"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <div className="mt-2 flex justify-between text-xs text-muted-foreground tabular-nums">
                      <span>Allocated {formatBalanceDays(Number(total))}</span>
                      <span>Used {formatBalanceDays(used)}</span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      )}
    </div>
  );
}
